import { cloneDeep } from 'lodash';
import { create } from 'zustand';

import { generateSelectEventType } from '@src/emp-3.0/components/custom-dimension/utils';
import { ICreateCustomDimensionPayload, IUpdateCustomDimensionPayload } from '@src/emp-3.0/types';
import {
  getEmptyTransformRuleGroup,
  getInitialActiveIndices,
} from '@components/data-transform-panel/utils/initializeUtils';
import { ERenameTitle, ERenamedDimension } from '@src/common/constant/enum';
import { ITransformRule } from '@src/common/types/empTypes';
import { ALL_APPLICABLE_MAPPED_EVENTS_TYPE } from '@src/emp-3.0/constants/dimensions';

type TCustomDimensionPayload =
  | ICreateCustomDimensionPayload
  | IUpdateCustomDimensionPayload
  | { [key: string]: string | string[] | number | undefined };

interface ILoadingStates {
  isCreateLoading: boolean;
  isUpdateLoading: boolean;
  isDeleteLoading: boolean;
}

interface ICustomDimensionPanelStore {
  selectEventType: string;
  setSelectEventType: (type: string) => void;
  customDimensionPayload: ICreateCustomDimensionPayload | IUpdateCustomDimensionPayload;
  setCustomDimensionPayload: (payload: TCustomDimensionPayload) => void;
  resetCustomDimensionStore: () => void;
  createFrom: 'kebab' | 'otherEntry';
  setCreateFrom: (createFrom: 'kebab' | 'otherEntry') => void;
  attributeFiltersPayload?: any[];
  setAttributeFiltersPayload: (filters?: any[]) => void;
  loadingStates: ILoadingStates;
  setLoadingStates: (loadingStates: ILoadingStates) => void;
}

const defaultCustomDimensionPayload: ICreateCustomDimensionPayload | IUpdateCustomDimensionPayload =
  {
    attribute_name: '',
    display_name: '',
    category: undefined,
    mapped_event_names: [],
    attribute_type: undefined,
    event_type: ALL_APPLICABLE_MAPPED_EVENTS_TYPE,
  };

function isDimensionNameChanged(dimension: TCustomDimensionPayload): boolean {
  return (
    dimension.display_name === undefined &&
    dimension.category === undefined &&
    dimension.attribute_type === undefined &&
    dimension.mapped_event_names === undefined
  );
}

export const useCustomDimensionStore = create<ICustomDimensionPanelStore>((set, get) => ({
  selectEventType: ALL_APPLICABLE_MAPPED_EVENTS_TYPE,
  customDimensionPayload: cloneDeep(defaultCustomDimensionPayload),
  attributeFiltersPayload: undefined,
  loadingStates: {
    isCreateLoading: false,
    isUpdateLoading: false,
    isDeleteLoading: false,
  },
  setLoadingStates: (loadingStates: ILoadingStates) => set(() => ({ loadingStates })),
  setAttributeFiltersPayload: (attributeFiltersPayload) => set(() => ({ attributeFiltersPayload })),
  setSelectEventType: (selectEventType) => set(() => ({ selectEventType })),
  setCustomDimensionPayload: (payload) =>
    set(() => {
      const { customDimensionPayload, selectEventType } = get();
      const { attribute_type, mapped_event_names } = customDimensionPayload;
      const attributeType = (payload.attribute_type || attribute_type) as number;
      const mappedEventNames = (
        isDimensionNameChanged(payload) ? [] : payload.mapped_event_names || mapped_event_names
      ) as string[];
      const newCustomDimensionPayload = {
        ...customDimensionPayload,
        ...payload,
        mapped_event_names: mappedEventNames,
      };
      return {
        customDimensionPayload: newCustomDimensionPayload,
        selectEventType:
          payload.mapped_event_names || payload.attribute_type
            ? generateSelectEventType(attributeType, mappedEventNames)
            : selectEventType,
      };
    }),
  resetCustomDimensionStore: () =>
    set(() => ({
      attributeFiltersPayload: undefined,
      customDimensionPayload: cloneDeep(defaultCustomDimensionPayload),
    })),
  createFrom: 'otherEntry',
  setCreateFrom: (createFrom) => set(() => ({ createFrom })),
}));
export interface ITransformItemActiveIndices {
  subAndIndex?: number;
  subOrIndex?: number;
}

export interface ITransformActiveIndices {
  orIndex?: number;
  metadataNameActive?: boolean;
  ruleItems?: ITransformItemActiveIndices[];
}

export interface IDataTransformPanelStore {
  transformPanelOpen: boolean;
  transformActiveIndices?: ITransformActiveIndices;
  transformTitle?: string;
  transformId?: number;
  transformName?: string;
  transformTargetDimension?: string;
  transformRule: ITransformRule[];
  loadingStates: ILoadingStates;
  setLoadingStates: (loadingStates: ILoadingStates) => void;
  setTransformActiveIndices: (transformActiveIndices?: ITransformActiveIndices) => void;
  setTransformTitle: (transformTitle?: string) => void;
  setTransformId: (transformId?: number) => void;
  setTransformName: (transformName?: string) => void;
  setTransformTargetDimension: (transformTargetDimension?: string) => void;
  setTransformRule: (transformRule: ITransformRule[]) => void;
  setTransformPanelOpen: (transformPanelOpen: boolean) => void;
  resetTransformStore: () => void;
}

export const useDataTransformPanelStore = create<IDataTransformPanelStore>((set) => ({
  transformPanelOpen: false,
  transformActiveIndices: getInitialActiveIndices(),
  transformTitle: ERenameTitle.PAGE,
  transformId: undefined,
  transformName: undefined,
  transformTargetDimension: ERenamedDimension.edited_page_title,
  transformRule: [getEmptyTransformRuleGroup()],
  loadingStates: {
    isCreateLoading: false,
    isUpdateLoading: false,
    isDeleteLoading: false,
  },
  setLoadingStates: (loadingStates: ILoadingStates) => set(() => ({ loadingStates })),
  setTransformActiveIndices: (transformActiveIndices) => set(() => ({ transformActiveIndices })),
  setTransformId: (transformId) => set(() => ({ transformId })),
  setTransformTitle: (transformTitle) => set(() => ({ transformTitle })),
  setTransformName: (transformName) => set(() => ({ transformName })),
  setTransformTargetDimension: (transformTargetDimension) =>
    set(() => ({ transformTargetDimension })),
  setTransformRule: (transformRule) => set(() => ({ transformRule })),
  setTransformPanelOpen: (transformPanelOpen) => set(() => ({ transformPanelOpen })),
  resetTransformStore: () =>
    set(() => ({
      transformActiveIndices: getInitialActiveIndices(),
      transformTitle: ERenameTitle.PAGE,
      transformId: undefined,
      transformName: undefined,
      transformTargetDimension: ERenamedDimension.edited_page_title,
      transformRule: [getEmptyTransformRuleGroup()],
    })),
}));
